// client/src/api.js
import axios from 'axios';
import { auth } from './firebase';

// --- Helpers ----------------------------------------------------
function getCookie(name) {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([$?*|{}\]\\^])/g, '\\$1') + '=([^;]*)')
  );
  return m ? decodeURIComponent(m[1]) : null;
}

async function ensureCsrfCookie(apiBase) {
  // Déjà présent ? on ne fait rien
  if (getCookie('XSRF-TOKEN')) return;
  // Récupère le cookie côté serveur custom (pas les CF)
  await fetch(`${apiBase}/api/csrf-token`, { credentials: 'include' });
}

// --- Base URL ---------------------------------------------------
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === 'production'
    ? 'https://ecobill.app'
    : 'http://localhost:5099');

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRF-Token',
});

// --- Interceptor: request --------------------------------------
api.interceptors.request.use(async (config) => {
  try {
    // 1) Auth Firebase -> Authorization: Bearer
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // 2) CSRF proactive: si mutation et pas de cookie -> va chercher le token AVANT d'envoyer
    const method = (config.method || 'get').toLowerCase();
    // Les routes device-link sont protégées par X-Requested-With (pas de cookie CSRF à récupérer)
    const isDeviceLink = String(config.url || '').startsWith('/api/device-link/');
    const needsCsrf = !isDeviceLink &&
      (method === 'post' || method === 'put' || method === 'patch' || method === 'delete');

    if (needsCsrf) {
      if (!getCookie('XSRF-TOKEN')) {
        await ensureCsrfCookie(API_BASE); // pose le cookie
      }
      // Pose le header (axios ne le fait pas en cross-origin)
      const csrf = getCookie('XSRF-TOKEN');
      if (csrf) {
        config.headers = config.headers || {};
        if (!config.headers['X-CSRF-Token']) {
          config.headers['X-CSRF-Token'] = csrf;
        }
      }
    }
  } catch {
    // no-op
  }
  return config;
});

// --- Interceptor: response (fallback si un 403 CSRF passe quand même) ---
api.interceptors.response.use(undefined, async (error) => {
  const { config, response } = error || {};
  if (!response || !config) return Promise.reject(error);

  if (
    !config._csrfRetried &&
    response.status === 403 &&
    String(response.data?.error || '').toLowerCase().includes('csrf')
  ) {
    try {
      config._csrfRetried = true;
      await ensureCsrfCookie(API_BASE);
      // Le prochain envoi aura le header via l'intercepteur request
      return api(config); // rejoue la requête initiale (une seule fois)
    } catch {
      // retombe dans le reject
    }
  }
  return Promise.reject(error);
});

export default api;
